import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from './cn'

export interface DropdownItem {
  id: string
  label: ReactNode
  onSelect?: () => void
  disabled?: boolean
  danger?: boolean
  icon?: ReactNode
}

export interface DropdownProps {
  trigger: ReactNode
  items: DropdownItem[]
  /** side the menu opens — defaults to `bottom-start`. */
  side?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'
  className?: string
  /** aligns trigger width to menu (useful for full-width triggers). */
  menuWidth?: string
  /**
   * Accessible name for the trigger button. Without it the name falls back to
   * the trigger's full textContent — which for rich triggers (avatar + user
   * name + role) is data-dependent and unusable for a11y and for the
   * screen-truth harvest (stable declarable names, trainer walk run 52).
   */
  triggerAriaLabel?: string
}

/**
 * Dropdown — accessible menu with outside-click + Escape close and keyboard nav.
 * Trigger is wrapped in a button (use IconButton or plain content as trigger).
 */
export function Dropdown({
  trigger,
  items,
  side = 'bottom-start',
  className = '',
  menuWidth = 'w-48',
  triggerAriaLabel,
}: DropdownProps) {
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState<number>(-1)
  const rootRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        const triggerEl = rootRef.current?.querySelector<HTMLButtonElement>('[data-dropdown-trigger]')
        triggerEl?.focus()
      }
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onEsc)
    firstItemRef.current?.focus()
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const onKeyDown = (e: React.KeyboardEvent) => {
    const enabled = items.filter((i) => !i.disabled)
    if (enabled.length === 0) return
    let next = focusIndex
    if (e.key === 'ArrowDown') next = (focusIndex + 1) % enabled.length
    else if (e.key === 'ArrowUp') next = (focusIndex - 1 + enabled.length) % enabled.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = enabled.length - 1
    else return
    e.preventDefault()
    setFocusIndex(next)
    const buttons = rootRef.current?.querySelectorAll<HTMLButtonElement>('[data-dropdown-item]')
    buttons?.[next]?.focus()
  }

  const sideClasses = {
    'bottom-start': 'top-full left-0 mt-1',
    'bottom-end': 'top-full right-0 mt-1',
    'top-start': 'bottom-full left-0 mb-1',
    'top-end': 'bottom-full right-0 mb-1',
  }[side]

  return (
    <div ref={rootRef} className={cn('relative inline-block', className)}>
      <button
        type="button"
        data-dropdown-trigger
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerAriaLabel}
        onClick={() => {
          setOpen(!open)
          setFocusIndex(0)
        }}
        className="inline-flex rounded-control focus-visible:outline-none focus-visible:shadow-focus"
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          aria-orientation="vertical"
          onKeyDown={onKeyDown}
          className={cn(
            'absolute z-dropdown rounded-control border border-line bg-white shadow-pop py-1 animate-fade-in motion-reduce:animate-none',
            menuWidth,
            sideClasses
          )}
        >
          {items.map((item, idx) => {
            const ref = idx === 0 ? firstItemRef : undefined
            return (
              <button
                key={item.id}
                ref={ref}
                type="button"
                data-dropdown-item
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return
                  item.onSelect?.()
                  setOpen(false)
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-fast ease-out-soft',
                  'focus-visible:outline-none focus-visible:shadow-focus focus:bg-whisper disabled:opacity-50 disabled:cursor-not-allowed',
                  item.danger ? 'text-err-ink hover:bg-err-bg' : 'text-ink-body hover:bg-whisper'
                )}
              >
                {item.icon && <span className="shrink-0">{item.icon}</span>}
                {item.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
